
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Check, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const PricingPlans = () => {
  const [isAnnual, setIsAnnual] = useState(false);
  const [activeModal, setActiveModal] = useState<string | null>(null);

  const plans = [
    {
      name: "Start",
      monthlyPrice: "R$ 29,90",
      annualPrice: "R$ 299,00",
      description: "Para corretores iniciantes",
      features: ["Edição automática com IA", "Correção de luz e contraste", "Acesso via celular", "5 edições por dia"],
      cta: "Assinar plano Start",
      popular: false
    },
    {
      name: "Pro",
      monthlyPrice: "R$ 69,90",
      annualPrice: "R$ 699,00",
      description: "Nosso plano mais popular",
      features: ["Edição automática com IA avançada", "Correção de luz, perspectiva e nitidez", "Acesso via celular e web", "15 edições por dia", "Desconto exclusivo com TocTocquers"],
      cta: "Assinar plano Pro",
      popular: true
    },
    {
      name: "Infinite",
      monthlyPrice: "R$ 97,90",
      annualPrice: "R$ 979,00",
      description: "Para corretores de alto volume",
      features: ["Todas as features do plano Pro", "Edições ilimitadas", "Prioridade no processamento", "Acesso prioritário ao CRM Toc Toc", "Descontos exclusivos com TocTocquers", "Suporte prioritário"],
      cta: "Assinar plano Infinite",
      popular: false
    }
  ];

  const openModal = (modalId: string) => {
    setActiveModal(modalId);
  };

  const closeModal = () => {
    setActiveModal(null);
  };

  const handleFormSubmit = () => {
    setTimeout(() => {
      closeModal();
    }, 1000);
  };

  return (
    <>
      <section id="planos" className="py-16 md:py-24 bg-toctoc-beige/30">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-toctoc-black mb-4">Planos Mensais Toc Rápido</h2>
            <p className="text-xl text-toctoc-gray-medium max-w-3xl mx-auto mb-8">
              Escolha o plano ideal para as suas necessidades
            </p>
            
            {/* Toggle Mensal/Anual */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <span className={`text-lg font-medium ${!isAnnual ? 'text-toctoc-blue' : 'text-gray-500'}`}>
                Mensal
              </span>
              <Switch
                checked={isAnnual}
                onCheckedChange={setIsAnnual}
                className="data-[state=checked]:bg-toctoc-blue"
              />
              <span className={`text-lg font-medium ${isAnnual ? 'text-toctoc-blue' : 'text-gray-500'}`}>
                Anual
              </span>
              {isAnnual && (
                <span className="bg-toctoc-orange text-white px-2 py-1 rounded-full text-sm font-medium">
                  Economize 17%
                </span>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <Card key={index} className={`border ${plan.popular ? 'border-toctoc-blue shadow-lg' : 'border-gray-200'} relative`}>
                {plan.popular && (
                  <span className="absolute top-0 right-0 bg-toctoc-blue text-white px-3 py-1 text-sm font-medium rounded-bl-lg rounded-tr-lg">
                    Popular
                  </span>
                )}
                <CardHeader className="pb-2">
                  <h3 className="text-2xl font-bold text-toctoc-black">{plan.name}</h3>
                  <p className="text-toctoc-gray-medium text-sm">{plan.description}</p>
                </CardHeader>
                <CardContent>
                  <div className="mt-4 mb-6">
                    <p className="text-4xl font-bold text-toctoc-black">
                      {isAnnual ? plan.annualPrice : plan.monthlyPrice}
                    </p>
                    <p className="text-toctoc-gray-medium">
                      por {isAnnual ? 'ano' : 'mês'}
                    </p>
                  </div>
                  <ul className="space-y-3">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <Check className="h-5 w-5 text-toctoc-blue mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-toctoc-gray-medium text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button 
                    onClick={() => openModal(`modal-${plan.name.toLowerCase()}`)}
                    className={`w-full rounded-full ${plan.popular ? 'bg-toctoc-orange hover:bg-toctoc-orange/90 text-white' : 'border-toctoc-blue text-toctoc-blue hover:bg-toctoc-blue/5'}`} 
                    variant={plan.popular ? 'default' : 'outline'}
                  >
                    {plan.cta}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          <div className="mt-16 bg-white p-8 rounded-xl border border-gray-100 shadow-sm">
            <h3 className="text-xl font-bold mb-4 text-center text-toctoc-black">Todos os planos incluem:</h3>
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="flex items-start">
                <Check className="h-5 w-5 text-toctoc-blue mr-2 mt-1 flex-shrink-0" />
                <p className="text-toctoc-gray-dark">Edição automática com IA especializada no mercado imobiliário</p>
              </div>
              <div className="flex items-start">
                <Check className="h-5 w-5 text-toctoc-blue mr-2 mt-1 flex-shrink-0" />
                <p className="text-toctoc-gray-dark">Correção de luz, perspectiva, nitidez e contraste</p>
              </div>
              <div className="flex items-start">
                <Check className="h-5 w-5 text-toctoc-blue mr-2 mt-1 flex-shrink-0" />
                <p className="text-toctoc-gray-dark">Acesso via celular, com envio fácil e retorno rápido</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modais para cada plano */}
      {plans.map((plan) => (
        activeModal === `modal-${plan.name.toLowerCase()}` && (
          <div key={plan.name} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeModal}>
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-toctoc-black">Assinar Plano {plan.name}</h2>
                <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <form 
                action="https://formsubmit.co/toctoctecnologia@gmail.com" 
                method="POST"
                onSubmit={handleFormSubmit}
                className="space-y-4"
              >
                <input 
                  name="name" 
                  type="text" 
                  placeholder="Nome completo" 
                  required 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-toctoc-blue"
                />
                <input 
                  name="phone" 
                  type="tel" 
                  placeholder="Telefone" 
                  required 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-toctoc-blue"
                />
                <input 
                  name="email" 
                  type="email" 
                  placeholder="E-mail" 
                  required 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-toctoc-blue"
                />
                <input name="plano" type="hidden" value={plan.name} />
                <input name="periodo" type="hidden" value={isAnnual ? 'Anual' : 'Mensal'} />
                <input type="hidden" name="_captcha" value="false" />
                <input type="hidden" name="_template" value="box" />
                <button 
                  type="submit" 
                  className="w-full bg-toctoc-orange hover:bg-toctoc-orange/90 text-white py-2 px-4 rounded-md font-semibold transition-colors"
                >
                  Confirmar Assinatura
                </button>
              </form>
            </div>
          </div>
        )
      ))}
    </>
  );
};

export default PricingPlans;
